import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useConnectivityStatus, { CONNECTIVITY_STATUS } from '../hooks/useConnectivityStatus';
import {
  OFFLINE_OPERATION_STATUS,
  OFFLINE_OPERATION_TYPE,
  listOfflineOperations,
  operationToOfflineSale,
  subscribeOfflineOperations,
  syncOfflineOperations,
} from '../lib/offlineOperationsQueue';
import { clearSaleAttempt, processSaleApi } from '../lib/salesApi';
import { syncOfflineCashOpen, syncOfflineCashReconcile } from '../lib/cashApi';
import { useProducts } from './ProductsContext';

const OfflineSyncContext = createContext(null);

export function OfflineSyncProvider({ currentUser, children }) {
  const connectivity = useConnectivityStatus();
  const { refreshProducts } = useProducts();
  const [operations, setOperations] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);
  const [lastSyncAt, setLastSyncAt] = useState(0);
  const [queueError, setQueueError] = useState('');

  const refreshQueue = useCallback(async () => {
    try {
      const records = await listOfflineOperations();
      setOperations(records);
      setQueueError('');
    } catch (error) {
      console.error('No se pudo leer la cola global offline:', error);
      setQueueError(error?.message || 'No se pudo leer la cola offline.');
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    void refreshQueue();
    return subscribeOfflineOperations(refreshQueue);
  }, [refreshQueue]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !currentUser?.uid || !navigator.storage?.persist) return;
    navigator.storage.persist().catch(() => false);
  }, [currentUser?.uid]);

  const syncNow = useCallback(async ({ silent = false } = {}) => {
    if (!currentUser?.uid || !connectivity.isOnline || syncingRef.current) return null;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      const summary = await syncOfflineOperations({
        [OFFLINE_OPERATION_TYPE.CASH_OPEN]: syncOfflineCashOpen,
        [OFFLINE_OPERATION_TYPE.CASH_RECONCILE]: syncOfflineCashReconcile,
        [OFFLINE_OPERATION_TYPE.SALE]: async (operation) => {
          const record = operationToOfflineSale(operation);
          return processSaleApi({
            saleDoc: record.saleDoc,
            storeId: record.storeId,
            idempotencyKey: record.idempotencyKey,
          });
        },
      }, {
        includeNeedsReview: !silent,
        shouldProcess: (operation) => !operation.ownerUid || operation.ownerUid === currentUser.uid,
        onProgress: (operation) => {
          if (operation.status === 'confirmed' && operation.type === OFFLINE_OPERATION_TYPE.SALE) {
            clearSaleAttempt(operation.payload?.idempotencyKey || operation.id);
          }
        },
      });

      setLastSyncAt(Date.now());
      await refreshQueue();
      if (summary?.confirmed > 0) {
        refreshProducts?.();
        if (!silent) {
          toast.success(`${summary.confirmed} operación${summary.confirmed === 1 ? '' : 'es'} sincronizada${summary.confirmed === 1 ? '' : 's'}.`);
        }
      }
      if (summary?.review > 0 && !silent) {
        toast.warn('Hay operaciones offline que requieren revisión antes de cerrar la caja.');
      }
      return summary;
    } catch (error) {
      console.error('No se pudieron sincronizar las operaciones offline:', error);
      if (!silent) toast.error(error?.message || 'No se pudo completar la sincronización.');
      return null;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [connectivity.isOnline, currentUser?.uid, refreshProducts, refreshQueue]);

  useEffect(() => {
    if (!connectivity.isOnline || !currentUser?.uid || loadingQueue || operations.length === 0) return;
    void syncNow({ silent: true });
  }, [connectivity.isOnline, currentUser?.uid, loadingQueue, operations.length, syncNow]);

  useEffect(() => {
    const hasRetryableOperations = operations.some(
      (operation) => operation.status === OFFLINE_OPERATION_STATUS.PENDING
    );
    if (!connectivity.isOnline || !currentUser?.uid || !hasRetryableOperations) return undefined;
    const intervalId = window.setInterval(() => {
      void syncNow({ silent: true });
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, [connectivity.isOnline, currentUser?.uid, operations, syncNow]);

  const counts = useMemo(() => operations.reduce((result, operation) => {
    result.total += 1;
    if (operation.status === OFFLINE_OPERATION_STATUS.NEEDS_REVIEW) result.review += 1;
    else if (operation.status === OFFLINE_OPERATION_STATUS.SYNCING) result.syncing += 1;
    else result.pending += 1;
    result.byType[operation.type] = (result.byType[operation.type] || 0) + 1;
    return result;
  }, { total: 0, pending: 0, syncing: 0, review: 0, byType: {} }), [operations]);

  const value = useMemo(() => ({
    ...connectivity,
    operations,
    counts,
    loadingQueue,
    isSyncing,
    lastSyncAt,
    queueError,
    refreshQueue,
    syncNow,
  }), [connectivity, operations, counts, loadingQueue, isSyncing, lastSyncAt, queueError, refreshQueue, syncNow]);

  return <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>;
}

export function useOfflineSync() {
  const context = useContext(OfflineSyncContext);
  if (!context) {
    return {
      status: CONNECTIVITY_STATUS.CHECKING,
      isOnline: typeof navigator === 'undefined' || navigator.onLine !== false,
      operations: [],
      counts: { total: 0, pending: 0, syncing: 0, review: 0, byType: {} },
      loadingQueue: true,
      isSyncing: false,
      queueError: '',
      syncNow: async () => null,
    };
  }
  return context;
}
